import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { BackLink } from "@/components/back-link";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { parseServiceHoldConflict, type ServiceHoldConflict } from "@/lib/service-hold";
import { customerSearchView, isSearchableCustomerQuery } from "@/lib/customer-search-state";
import { openExternalLink } from "@/lib/standalone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PageSpinner } from "@/components/ui/spinner";
import { Dialog } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
    ArrowLeft,
    Save,
    Search,
    Plus,
    X,
    Building2,
    ExternalLink,
    AlertTriangle,
    Info,
    ChevronDown,
    ShieldCheck,
    MapPin,
    Receipt,
} from "lucide-react";
import { deriveJobTypeCodeFromEstimateLines, type EstimatePrefill, type EstimatePrefillLine } from "./estimatePrefill";

interface JobType {
    id: string;
    code: string;
    name: string;
    trade_id: string;
    trade_name: string;
    default_duration_minutes: number;
    /** base + per-device duration model (216). Null on legacy rows. */
    base_minutes?: number | null;
    per_device_minutes?: number | null;
    priority: "low" | "normal" | "high";
    is_active: boolean;
}

/**
 * Mirror of the server estimator in jobDurationEstimator.ts:
 *   base + per_device * max(deviceCount, 1), falling back to the flat
 *   default when the model fields aren't set. At New-Job time we usually
 *   don't know the device count yet, so we floor to one unit of work.
 */
function estimateJobMinutes(jt: JobType, deviceCount = 0): number {
    const base = jt.base_minutes;
    const perDevice = jt.per_device_minutes;
    if (base == null || perDevice == null) {
        return Math.max(5, Math.round(jt.default_duration_minutes));
    }
    const devices = Math.max(1, Math.floor(deviceCount));
    return Math.max(5, Math.round(base + perDevice * devices));
}

interface CustomerOption {
    id: string;
    company_name: string;
}

interface SiteOption {
    id: string;
    name: string;
    address_line: string;
    city: string;
}

interface JobDetail {
    id: string;
    title: string;
    status: string;
    customer_id: string | null;
    contact_id: string | null;
    trade_id: string | null;
    job_type_id?: string | null;
    job_type_name?: string | null;
    job_type_code?: string | null;
    job_type_default_duration_minutes?: number | null;
    site_id: string | null;
    notes: string | null;
    tech_instructions?: string | null;
    scheduled_start: string | null;
    scheduled_end: string | null;
    assigned_to: string | null;
    kind?: string | null;
    target_date?: string | null;
    source_estimate_id?: string | null;
    scheduling_flexibility?: SchedulingFlexibility | null;
    earliest_start_at?: string | null;
    latest_finish_at?: string | null;
    preferred_window?: PreferredWindow | null;
    customer_notes_scheduling?: string | null;
    due_by_date?: string | null;
    allowed_weekdays?: number[] | null;
}

/** Pattern-match against the job_types.code prefix to decide whether
 *  this is a compliance job (backflow test/repair/replace, fire
 *  extinguisher / fire-safety inspection). Keeps the office UI in
 *  sync with the server-side `deriveJobDueByFromAssets` helper as new
 *  compliance job types are added. */
const COMPLIANCE_JOB_TYPE_PREFIXES = ["backflow_", "fire_ext_", "fire_safety_"];

function isComplianceJobTypeCode(code: string | undefined | null): boolean {
    if (!code) return false;
    const c = code.toLowerCase();
    return COMPLIANCE_JOB_TYPE_PREFIXES.some((p) => c.startsWith(p));
}

/** ISO weekday labels for the "Available weekdays" chip toggles.
 *  ISO weekday 1=Mon..7=Sun matches the value stored in
 *  jobs.allowed_weekdays. */
const WEEKDAY_CHIPS: { value: number; short: string; long: string }[] = [
    { value: 1, short: "Mon", long: "Monday" },
    { value: 2, short: "Tue", long: "Tuesday" },
    { value: 3, short: "Wed", long: "Wednesday" },
    { value: 4, short: "Thu", long: "Thursday" },
    { value: 5, short: "Fri", long: "Friday" },
    { value: 6, short: "Sat", long: "Saturday" },
    { value: 7, short: "Sun", long: "Sunday" },
];

/** Choose the earliest non-null next_due_date out of an asset list,
 *  treating top-level (parent_asset_id IS NULL) assets as the in-scope
 *  ones — matches the server-side auto-link rule used at job create. */
function earliestNextDue(
    assets:
        Array<{ next_due_date?: string | null; parent_asset_id?: string | null; status?: string | null }> | undefined,
): string | null {
    if (!assets || assets.length === 0) return null;
    const candidates = assets
        .filter((a) => (a.parent_asset_id ?? null) === null && a.status !== "inactive")
        .map((a) => a.next_due_date ?? null)
        .filter((v): v is string => !!v);
    if (candidates.length === 0) return null;
    return candidates.sort()[0] ?? null;
}

type SchedulingFlexibility = "flexible" | "specific_day" | "specific_window" | "hard_appointment";
type PreferredWindow = "AM" | "PM" | "EVENING" | "ANYTIME";

const FLEX_OPTIONS: {
    value: SchedulingFlexibility;
    label: string;
    helper: string;
}[] = [
    {
        value: "flexible",
        label: "Any day, any time",
        helper: "We'll fit this into the best available day and route.",
    },
    {
        value: "specific_day",
        label: "Specific day, any time",
        helper: "Pinned to a date you pick, but any time that day works.",
    },
    {
        value: "specific_window",
        label: "Specific day and time window",
        helper: "Customer needs a morning, afternoon, or exact time range.",
    },
    {
        value: "hard_appointment",
        label: "Fixed appointment",
        helper: "Customer is meeting the tech. This won't be moved by auto-scheduling.",
    },
];

interface SiteDetail {
    id: string;
    customer_id: string;
    name: string;
    /** Legacy ST location code — never shown as the portal account. */
    account_number: string | null;
    /** Canonical portal account (`sites.water_account_number`). */
    water_account_number?: string | null;
    water_provider_id: string | null;
    water_provider_name: string | null;
    water_provider_slug: string | null;
    water_provider_submission_platform: string | null;
    water_provider_is_portal?: boolean | null;
    water_provider_portal_url: string | null;
    default_contact_id: string | null;
    site_contact_first_name?: string | null;
    site_contact_last_name?: string | null;
    site_contact_phone?: string | null;
    site_contact_email?: string | null;
    site_contact_role?: string | null;
}

function formatDuration(minutes: number): string {
    if (minutes < 60) return `${minutes} min`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function addMinutesToLocalDatetime(localDatetime: string, minutes: number): string {
    if (!localDatetime) return "";
    const start = new Date(localDatetime);
    if (Number.isNaN(start.getTime())) return "";
    const end = new Date(start.getTime() + minutes * 60_000);
    const offset = end.getTimezoneOffset();
    const local = new Date(end.getTime() - offset * 60_000);
    return local.toISOString().slice(0, 16);
}

function toLocalDatetimeValue(iso: string | null | undefined): string {
    if (!iso) return "";
    const d = new Date(iso);
    const offset = d.getTimezoneOffset();
    const local = new Date(d.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16);
}

/** YYYY-MM-DD slice of a stored ISO timestamp, in local time. */
function toLocalDateOnly(iso: string | null | undefined): string {
    const local = toLocalDatetimeValue(iso);
    return local ? (local.split("T")[0] ?? "") : "";
}

type TimeOfDayMode = "morning" | "afternoon" | "all_day" | "custom";

/** Visible ranges that back the morning/afternoon/all-day chips for the
 *  "Specific day and time window" strategy. The auto-scheduler reads
 *  earliest/latest as a hard window plus preferred_window for ranking. */
const TIME_WINDOW_RANGES: Record<
    Exclude<TimeOfDayMode, "custom">,
    { start: string; end: string; preferredWindow: PreferredWindow }
> = {
    morning: { start: "07:00", end: "12:00", preferredWindow: "AM" },
    afternoon: { start: "12:00", end: "17:00", preferredWindow: "PM" },
    all_day: { start: "07:00", end: "17:00", preferredWindow: "ANYTIME" },
};

const TIME_OF_DAY_OPTIONS: { value: TimeOfDayMode; label: string }[] = [
    { value: "morning", label: "Morning" },
    { value: "afternoon", label: "Afternoon" },
    { value: "all_day", label: "All day" },
    { value: "custom", label: "Custom" },
];

function useDebounce(value: string, delay: number): string {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(timer);
    }, [value, delay]);
    return debounced;
}

export default function JobFormPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const qc = useQueryClient();
    const isEdit = !!id && id !== "new";
    const [searchParams] = useSearchParams();
    const prefillCustomerId = searchParams.get("customerId") ?? "";
    const prefillSiteId = searchParams.get("siteId") ?? "";
    const prefillEstimateId = searchParams.get("estimateId") ?? "";

    const [title, setTitle] = useState("");
    // Once the user types in the Title field we stop auto-deriving it from
    // Job Type + Customer/Location so their wording always wins.
    const [titleManuallyEdited, setTitleManuallyEdited] = useState(false);
    const [customerId, setCustomerId] = useState(prefillCustomerId);
    const [customerName, setCustomerName] = useState("");
    const [jobTypeId, setJobTypeId] = useState("");
    const [siteId, setSiteId] = useState(prefillSiteId);
    const [notes, setNotes] = useState("");
    // #813: the office-authored on-site brief (jobs.summary) — the FIRST thing
    // the tech reads. Create-only here; edits live on the job page summary card.
    const [summary, setSummary] = useState("");
    // #887 — customer PO / WO reference + the statement-safe description, all
    // optional, settable at create so maintenance-account jobs are born with
    // the reference their AP pays against.
    const [customerPo, setCustomerPo] = useState("");
    const [externalRef, setExternalRef] = useState("");
    const [customerFacingDescription, setCustomerFacingDescription] = useState("");
    const [accountNumber, setAccountNumber] = useState("");
    // #825 M11: a stored portal account renders read-only until the office
    // clicks Edit — a live compliance identifier shouldn't sit in a plainly
    // editable box a day-one hire can nudge by accident.
    const [editingAccount, setEditingAccount] = useState(false);
    // Captures the site's stored account number when we load site detail so
    // we only fire a PATCH when the office actually changed it.
    const siteAccountNumberRef = useRef<string | null>(null);
    const [scheduledStart, setScheduledStart] = useState("");
    const [scheduledEnd, setScheduledEnd] = useState("");
    // ─── Scheduling flexibility (intake-captured) ─────────────────────
    const [flexibility, setFlexibility] = useState<SchedulingFlexibility>("flexible");
    const [customerSchedulingNotes, setCustomerSchedulingNotes] = useState("");
    // ─── Compliance deadline + customer availability ──────────────────
    // Required for backflow + fire-safety jobs (auto-derived server-side
    // from MIN(asset.next_due_date) when blank, but the office picks it
    // explicitly here so the at-risk pill on the dispatch backlog is
    // accurate from the moment the job is created).
    const [dueBy, setDueBy] = useState("");
    // ISO weekday set (1=Mon..7=Sun). Empty = "any day". Stored as
    // sorted array on submit; collapsed to null when all 7 are
    // selected so the auto-scheduler short-circuits cleanly.
    const [allowedWeekdays, setAllowedWeekdays] = useState<Set<number>>(new Set());
    // Office UI: AM / PM / All-day. EVENING is intentionally omitted
    // until the field-tech intake form catches up — keeps the column
    // values resolvable for power users while not muddying the
    // common-case pick.
    const [preferredTimeOfDay, setPreferredTimeOfDay] = useState<"AM" | "PM" | "ANYTIME" | "">("");
    // ─── Strategy-specific scheduling controls ────────────────────────
    // Single source of truth for "the day this job belongs on" used by
    // both `specific_day` and `specific_window`. We deliberately don't
    // reuse scheduledStart for the date because that field has its own
    // semantics (a hard pin) under `hard_appointment` and
    // `specific_day` legacy storage.
    const [scheduleDate, setScheduleDate] = useState("");
    const [timeOfDayMode, setTimeOfDayMode] = useState<TimeOfDayMode>("morning");
    // HH:MM (24-hour) — only used when timeOfDayMode === "custom" under
    // `specific_window`. Kept separate from scheduledStart/End so the
    // dispatcher can switch back to a preset window without losing
    // their custom values.
    const [customStartTime, setCustomStartTime] = useState("09:00");
    const [customEndTime, setCustomEndTime] = useState("11:00");
    // Optional Scheduling Constraints accordion. Auto-expanded after we
    // load an existing job that already has any constraint values so
    // the dispatcher sees what's there without having to open it.
    const [constraintsOpen, setConstraintsOpen] = useState(false);
    // Tracks whether the office has manually set the due-by date so we
    // don't keep clobbering an explicit value with the asset auto-fill
    // hint when the site list reloads.
    const dueByTouchedRef = useRef(false);
    const [autoDueByHint, setAutoDueByHint] = useState<string | null>(null);
    // Tracks whether the user has manually edited the end time, so we
    // never clobber their explicit choice with the auto-fill from the
    // job type's default duration.
    const scheduledEndTouchedRef = useRef(false);

    // Customer typeahead
    const [customerSearch, setCustomerSearch] = useState("");
    const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
    const [showNewCustomer, setShowNewCustomer] = useState(false);
    const [newCustomerName, setNewCustomerName] = useState("");
    const customerRef = useRef<HTMLDivElement>(null);
    const debouncedSearch = useDebounce(customerSearch, 300);

    // Site typeahead — large multi-site customers can have 1,000+ locations,
    // so the picker searches server-side instead of rendering a giant <select>.
    const [siteSearch, setSiteSearch] = useState("");
    const [siteDropdownOpen, setSiteDropdownOpen] = useState(false);
    const siteRef = useRef<HTMLDivElement>(null);
    const debouncedSiteSearch = useDebounce(siteSearch, 250);

    const { data: existing, isLoading: loadingJob } = useQuery({
        queryKey: ["job", id],
        queryFn: () => api.get<{ job: JobDetail }>(`/api/jobs/${id}`),
        enabled: isEdit,
    });

    useEffect(() => {
        const job = existing?.job;
        if (!job) return;
        setTitle(job.title ?? "");
        // Existing titles are user-owned; never auto-overwrite them.
        setTitleManuallyEdited(true);
        setCustomerId(job.customer_id ?? "");
        setJobTypeId(job.job_type_id ?? "");
        setSiteId(job.site_id ?? "");
        setNotes(job.notes || job.tech_instructions || "");
        setScheduledStart(toLocalDatetimeValue(job.scheduled_start));
        setScheduledEnd(toLocalDatetimeValue(job.scheduled_end));
        // When loading an existing job, treat any pre-set end time
        // as user-owned so we don't overwrite it on first render.
        scheduledEndTouchedRef.current = !!job.scheduled_end;
        const flex = (job.scheduling_flexibility as SchedulingFlexibility | null) ?? "flexible";
        setFlexibility(flex);
        setCustomerSchedulingNotes(job.customer_notes_scheduling ?? "");
        setDueBy(job.due_by_date ?? "");
        // Anything stored is "user-owned" — don't overwrite with the
        // asset auto-fill hint when the site detail loads.
        dueByTouchedRef.current = !!job.due_by_date;
        const stored = Array.isArray(job.allowed_weekdays) ? job.allowed_weekdays : [];
        setAllowedWeekdays(new Set(stored));
        const w = job.preferred_window as PreferredWindow | null;
        setPreferredTimeOfDay(w === "AM" || w === "PM" || w === "ANYTIME" ? w : "");

        // ─── Derive new strategy-specific UI state ───────────────────
        // `specific_day` historically stored a soft pin in scheduled_start
        // at 08:00 of the chosen day. Read the date back out of either
        // scheduled_start or earliest_start_at.
        if (flex === "specific_day") {
            setScheduleDate(toLocalDateOnly(job.scheduled_start) || toLocalDateOnly(job.earliest_start_at));
        } else if (flex === "specific_window") {
            const startLocal = toLocalDatetimeValue(job.scheduled_start);
            const endLocal = toLocalDatetimeValue(job.scheduled_end);
            // If both scheduled_start and scheduled_end are set, treat as
            // a custom window (the picker we use for irregular ranges).
            if (startLocal && endLocal) {
                setScheduleDate(startLocal.split("T")[0] ?? "");
                setTimeOfDayMode("custom");
                setCustomStartTime(startLocal.split("T")[1] ?? "09:00");
                setCustomEndTime(endLocal.split("T")[1] ?? "11:00");
            } else {
                setScheduleDate(toLocalDateOnly(job.earliest_start_at) || toLocalDateOnly(job.scheduled_start));
                if (w === "AM") setTimeOfDayMode("morning");
                else if (w === "PM") setTimeOfDayMode("afternoon");
                else setTimeOfDayMode("all_day");
            }
        } else {
            // flexible / hard_appointment: nothing to derive for the day
            // picker; keep defaults so switching strategies doesn't
            // surface stale values.
            setScheduleDate("");
        }

        // Auto-expand the constraints accordion when there's already
        // anything in it, so the dispatcher sees what's saved without
        // having to discover it.
        const hasConstraintValues =
            stored.length > 0 ||
            (flex !== "specific_window" && (w === "AM" || w === "PM" || w === "ANYTIME")) ||
            !!(job.customer_notes_scheduling ?? "").trim();
        if (hasConstraintValues) setConstraintsOpen(true);
    }, [existing]);

    const { data: jobTypesData } = useQuery({
        queryKey: ["job-types", "active"],
        queryFn: () => api.get<{ jobTypes: JobType[] }>("/api/job-types?activeOnly=true"),
    });

    // ─── Estimate → New Job prefill (/jobs/new?estimateId=…) ─────────
    // Arriving from the Approved — ready to schedule tab: pull the
    // estimate, prefill customer/site/title, derive the job type from
    // its lines, and link the job back via payload.estimateId.
    const { data: estimatePrefillData } = useQuery({
        queryKey: ["estimate-prefill", prefillEstimateId],
        queryFn: () =>
            api.get<{ estimate: EstimatePrefill; lines: EstimatePrefillLine[] }>(`/api/estimates/${prefillEstimateId}`),
        enabled: !isEdit && !!prefillEstimateId,
    });
    const sourceEstimate = !isEdit && prefillEstimateId ? (estimatePrefillData?.estimate ?? null) : null;
    const estimatePrefillAppliedRef = useRef(false);
    useEffect(() => {
        if (isEdit || estimatePrefillAppliedRef.current) return;
        const est = estimatePrefillData?.estimate;
        if (!est) return;
        const types = jobTypesData?.jobTypes;
        if (!types) return;
        estimatePrefillAppliedRef.current = true;
        setCustomerId(est.customer_id);
        if (est.site_id) setSiteId(est.site_id);
        if (est.title) {
            setTitle(est.title);
            // The estimate title describes the approved scope — keep it.
            setTitleManuallyEdited(true);
        }
        const code = deriveJobTypeCodeFromEstimateLines(estimatePrefillData?.lines ?? []);
        if (code) {
            const jt = types.find((t) => t.code === code);
            if (jt) setJobTypeId(jt.id);
        }
    }, [isEdit, estimatePrefillData, jobTypesData]);

    const { data: customerResults, isFetching: customerSearchFetching } = useQuery({
        queryKey: ["customer-search", debouncedSearch],
        queryFn: () =>
            api.get<{ customers: CustomerOption[] }>(
                `/api/customers?q=${encodeURIComponent(debouncedSearch)}&limit=20`,
            ),
        enabled: isSearchableCustomerQuery(debouncedSearch),
    });

    // Prefilled customer lookup — when arriving via /jobs/new?customerId=…
    const { data: prefilledCustomer } = useQuery({
        queryKey: ["customer", prefillCustomerId, "lookup"],
        queryFn: () =>
            api.get<{ customer: { id: string; company_name: string } }>(`/api/customers/${prefillCustomerId}`),
        enabled: !!prefillCustomerId && !customerName,
    });

    useEffect(() => {
        if (prefilledCustomer?.customer && !customerName) {
            setCustomerName(prefilledCustomer.customer.company_name);
        }
    }, [prefilledCustomer, customerName]);

    // Edit-mode customer lookup — when editing a job, customerId comes from the
    // job record but isn't in the current search results, so resolve its name
    // here (otherwise the chip would show the raw UUID).
    const { data: editCustomer } = useQuery({
        queryKey: ["customer", customerId],
        queryFn: () => api.get<{ customer: { id: string; company_name: string } }>(`/api/customers/${customerId}`),
        enabled: !!customerId && !customerName,
    });

    useEffect(() => {
        if (editCustomer?.customer && !customerName) {
            setCustomerName(editCustomer.customer.company_name);
        }
    }, [editCustomer, customerName]);

    const { data: sitesData } = useQuery({
        queryKey: ["customer-sites", customerId, debouncedSiteSearch],
        queryFn: () => {
            const params = new URLSearchParams();
            params.set("limit", "20");
            if (debouncedSiteSearch.trim()) params.set("q", debouncedSiteSearch.trim());
            return api.get<{ sites: SiteOption[]; total: number }>(
                `/api/customers/${customerId}/sites?${params.toString()}`,
            );
        },
        enabled: !!customerId,
    });

    // Single-site customers auto-select with zero clicks. Only when not
    // editing, nothing already chosen, and no active search narrowing the set.
    useEffect(() => {
        if (isEdit) return;
        if (siteId) return;
        if (debouncedSiteSearch.trim()) return;
        if (sitesData && sitesData.total === 1 && sitesData.sites[0]) {
            setSiteId(sitesData.sites[0].id);
        }
    }, [sitesData, isEdit, siteId, debouncedSiteSearch]);

    // Pull the full site record when the office picks one. We need the
    // jurisdiction (`water_providers.submission_platform`/`portal_url`) and the
    // stored `account_number` for the Reporting Setup card.
    const { data: siteDetailData } = useQuery({
        queryKey: ["site-detail", siteId],
        queryFn: () => api.get<{ site: SiteDetail }>(`/api/sites/${siteId}`),
        enabled: !!siteId,
    });
    const siteDetail = siteDetailData?.site ?? null;

    // Sync local `accountNumber` input whenever a fresh site loads so the
    // office sees whatever the site currently has on file. We stash the
    // baseline so submit can decide if a PATCH is actually needed.
    // Canonical column only (`water_account_number`) — the legacy ST code
    // is never surfaced as the portal account.
    useEffect(() => {
        if (!siteDetail) {
            siteAccountNumberRef.current = null;
            return;
        }
        const stored = siteDetail.water_account_number ?? "";
        siteAccountNumberRef.current = stored;
        setAccountNumber(stored);
        // #825 M11: a stored compliance identifier re-locks whenever a fresh
        // site loads — editing it is a deliberate click, never a stray tab.
        setEditingAccount(false);
    }, [siteDetail?.id, siteDetail?.water_account_number]);

    // Load customer name when editing
    useEffect(() => {
        if (customerId && !customerName) {
            const results = customerResults?.customers;
            const found = results?.find((c) => c.id === customerId);
            if (found) setCustomerName(found.company_name);
        }
    }, [customerId, customerResults, customerName]);

    // When the customer changes (after the initial prefill), reset siteId so it
    // doesn't keep stale values across selections.
    const prevCustomerIdRef = useRef(customerId);
    useEffect(() => {
        if (isEdit) return;
        const prev = prevCustomerIdRef.current;
        if (prev && prev !== customerId) setSiteId("");
        prevCustomerIdRef.current = customerId;
    }, [customerId, isEdit]);

    // Close dropdown on click outside
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (customerRef.current && !customerRef.current.contains(e.target as Node)) {
                setCustomerDropdownOpen(false);
            }
            if (siteRef.current && !siteRef.current.contains(e.target as Node)) {
                setSiteDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const jobTypes: JobType[] = jobTypesData?.jobTypes ?? [];
    const selectedJobType = jobTypes.find((jt) => jt.id === jobTypeId) ?? null;
    const isComplianceJobType = isComplianceJobTypeCode(selectedJobType?.code ?? null);
    // #812 (audit M13): a replacement's deadline is the swap date the office
    // agreed with the customer, NOT the old device's next test date — so
    // replacement job types never auto-fill Due By from device next-due.
    const isReplacementJobType = (selectedJobType?.code ?? "").toLowerCase().includes("replace");

    // Pull the site's assets only when we need the auto-fill — keeps the
    // common "office, no compliance job" case from issuing an extra
    // request. The endpoint already returns next_due_date so we just
    // pick MIN over the in-scope (top-level) assets.
    interface SiteAssetRow {
        id: string;
        name: string;
        status: string | null;
        parent_asset_id: string | null;
        next_due_date: string | null;
    }
    const { data: siteAssetsData } = useQuery({
        queryKey: ["site-assets-for-due-fill", siteId],
        queryFn: () => api.get<{ assets: SiteAssetRow[] }>(`/api/sites/${siteId}/assets?limit=200`),
        enabled: !!siteId && isComplianceJobType,
    });
    useEffect(() => {
        if (!isComplianceJobType || isReplacementJobType) {
            setAutoDueByHint(null);
            return;
        }
        const earliest = earliestNextDue(siteAssetsData?.assets);
        setAutoDueByHint(earliest);
        if (!earliest) return;
        // Only pre-fill if the office hasn't manually edited dueBy.
        if (dueByTouchedRef.current) return;
        if (dueBy) return;
        setDueBy(earliest);
    }, [isComplianceJobType, isReplacementJobType, siteAssetsData, dueBy]);
    // Group active job types by trade name for the optgroup picker.
    const jobTypesByTrade: { trade: string; types: JobType[] }[] = (() => {
        const groups = new Map<string, JobType[]>();
        for (const jt of jobTypes) {
            const arr = groups.get(jt.trade_name) ?? [];
            arr.push(jt);
            groups.set(jt.trade_name, arr);
        }
        return Array.from(groups.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([trade, types]) => ({
                trade,
                types: types.slice().sort((a, b) => a.name.localeCompare(b.name)),
            }));
    })();
    const customers = customerResults?.customers ?? [];
    // #822: the dropdown renders from this verdict, never from customers.length
    // alone — a pending search must not show stale rows or "No customers found".
    const customerView = customerSearchView({
        inputText: customerSearch,
        debouncedText: debouncedSearch,
        isFetching: customerSearchFetching,
        resultCount: customers.length,
    });
    const sites = sitesData?.sites ?? [];
    // Label for the chosen site. Prefer the loaded site detail; fall back to
    // the row in the current search page if detail hasn't arrived yet.
    const selectedSiteLabel = siteId
        ? (siteDetail?.name ??
          (() => {
              const s = sites.find((x) => x.id === siteId);
              return s ? `${s.name}${s.city ? ` — ${s.city}` : ""}` : "Selected location";
          })())
        : "";

    // Auto-derive the Title from Job Type + Customer/Location until the user
    // takes over. Keying off the Job Type guarantees a non-empty value (Job
    // Type is required) so the server's title min(1) rule is always met, while
    // the field stays fully editable.
    useEffect(() => {
        if (isEdit || titleManuallyEdited) return;
        const jt = selectedJobType?.name?.trim();
        if (!jt) return;
        const loc = (selectedSiteLabel || customerName).trim();
        const auto = loc ? `${jt} — ${loc}` : jt;
        setTitle((prev) => (prev === auto ? prev : auto));
    }, [isEdit, titleManuallyEdited, selectedJobType, selectedSiteLabel, customerName]);

    // Auto-fill the end time when the job type or start time changes,
    // but only when the user hasn't already typed in their own end.
    useEffect(() => {
        if (!selectedJobType || !scheduledStart) return;
        if (scheduledEndTouchedRef.current) return;
        const auto = addMinutesToLocalDatetime(scheduledStart, estimateJobMinutes(selectedJobType));
        if (auto && auto !== scheduledEnd) setScheduledEnd(auto);
    }, [selectedJobType, scheduledStart, scheduledEnd]);

    const selectCustomer = useCallback((c: CustomerOption) => {
        setCustomerId(c.id);
        setCustomerName(c.company_name);
        setCustomerSearch("");
        setCustomerDropdownOpen(false);
    }, []);

    const clearCustomer = useCallback(() => {
        setCustomerId("");
        setCustomerName("");
        setSiteId("");
        setCustomerSearch("");
    }, []);

    const createCustomerMutation = useMutation({
        mutationFn: (name: string) => api.post<{ id: string }>("/api/customers", { companyName: name }),
        onSuccess: (data) => {
            setCustomerId(data.id);
            setCustomerName(newCustomerName);
            setNewCustomerName("");
            setShowNewCustomer(false);
            qc.invalidateQueries({ queryKey: ["customer-search"] });
        },
    });

    // #1030 — the customer is on service hold ("Do Not Service"). The server
    // answers 409 rather than creating the job; we hold the exact payload so a
    // manager who decides to work it anyway resends the same job with an
    // override reason, instead of re-filling the form.
    const [holdConflict, setHoldConflict] = useState<
        (ServiceHoldConflict & { payload: Record<string, unknown> }) | null
    >(null);
    const [holdOverrideReason, setHoldOverrideReason] = useState("");

    const mutation = useMutation({
        mutationFn: async (payload: Record<string, unknown>) => {
            if (isEdit) {
                await api.patch(`/api/jobs/${id}`, payload);
                return { id: id! };
            }
            return api.post<{ id: string }>("/api/jobs", payload);
        },
        onError: (err, payload) => {
            const hold = err instanceof ApiError ? parseServiceHoldConflict(err.body) : null;
            if (hold) {
                setHoldOverrideReason("");
                setHoldConflict({ ...hold, payload });
            }
        },
        onSuccess: (data) => {
            setHoldConflict(null);
            qc.invalidateQueries({ queryKey: ["jobs"] });
            if (isEdit) qc.invalidateQueries({ queryKey: ["job", id] });
            // H7: a freshly-created (or rescheduled) job must show up in the
            // dispatch backlog/grid without a manual refresh.
            qc.invalidateQueries({ queryKey: ["dispatch"] });
            qc.invalidateQueries({ queryKey: ["work-pulse"] });
            navigate(`/jobs/${data.id}`);
        },
    });

    // Persist the portal account number on the site itself so every future
    // job at this property inherits the same number. Runs in parallel with
    // job create/update; failure should not block the main save flow.
    const patchSiteAccount = async (nextValue: string) => {
        if (!siteId) return;
        const baseline = siteAccountNumberRef.current ?? "";
        const trimmed = nextValue.trim();
        if (trimmed === baseline.trim()) return;
        try {
            // Canonical portal-account write (sites.water_account_number).
            await api.patch(`/api/sites/${siteId}`, {
                waterAccountNumber: trimmed.length === 0 ? null : trimmed,
            });
            siteAccountNumberRef.current = trimmed;
            qc.invalidateQueries({ queryKey: ["site-detail", siteId] });
            qc.invalidateQueries({ queryKey: ["site-water", siteId] });
        } catch (err) {
            toast.warning(
                "Saved the job, but couldn't save the account number on the site. Try editing it from the site record.",
            );
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isEdit) {
            if (!customerId || !siteId) {
                toast.error("Select a customer and site before creating the job.");
                return;
            }
        }
        // ─── Per-strategy validation ─────────────────────────────────
        // Each strategy collects different inputs; reject inline before
        // hitting the server so the office gets a clear inline message.
        if (flexibility === "specific_day" && !scheduleDate) {
            toast.error("Pick a date for the visit.");
            return;
        }
        if (flexibility === "specific_window") {
            if (!scheduleDate) {
                toast.error("Pick a date for the visit.");
                return;
            }
            if (timeOfDayMode === "custom" && (!customStartTime || !customEndTime)) {
                toast.error("Set both a start and end time for the custom window.");
                return;
            }
            if (timeOfDayMode === "custom" && customStartTime >= customEndTime) {
                toast.error("Custom end time must be after the start time.");
                return;
            }
        }
        if (flexibility === "hard_appointment" && (!scheduledStart || !scheduledEnd)) {
            toast.error("Pick a start and end time for the appointment.");
            return;
        }
        // Job type is required so pricing, duration, compliance, and the
        // device cascade all key off a known type. The picker stays optional
        // in the markup, so block here with a clear message.
        if (!jobTypeId) {
            toast.error("Pick a job type so pricing, duration, and compliance rules apply.");
            return;
        }
        // Compliance jobs MUST carry a due-by date so the auto-scheduler
        // can honour the regulator's clock. Block the save with a clear
        // toast rather than letting the server reject a partial payload.
        if (isComplianceJobType && !dueBy) {
            toast.error(
                "Pick a Due-by date — backflow & fire-safety jobs need a deadline so the auto-scheduler doesn't slip past it.",
            );
            return;
        }

        const payload: Record<string, unknown> = { title };
        if (jobTypeId) payload.jobTypeId = jobTypeId;
        // Notes are an append-only thread now — the form only seeds the
        // first entry at CREATE. Edits never PATCH jobs.notes (that column
        // is a read-only mirror of the pinned thread note).
        if (!isEdit && notes) payload.notes = notes;
        // Summary is create-only on this form; after that it's edited on the
        // job page's Summary card, so edit mode never clobbers it here.
        if (!isEdit && summary.trim()) payload.summary = summary.trim();
        // PO/WO + customer description are create-only here too — after
        // create they're edited on the job page's Details card (#887).
        if (!isEdit && customerPo.trim()) payload.customerPo = customerPo.trim();
        if (!isEdit && externalRef.trim()) payload.externalRef = externalRef.trim();
        if (!isEdit && customerFacingDescription.trim())
            payload.customerFacingDescription = customerFacingDescription.trim();
        // Link the job back to its source estimate (jobs.source_estimate_id)
        // so the estimate leaves the ready-to-schedule list and the approval
        // record travels with the job.
        if (!isEdit && prefillEstimateId) payload.estimateId = prefillEstimateId;
        // ─── Customer / site / contact diff ──────────────────────────
        // On create, send whichever values the user picked. On edit,
        // only send when the value actually changed so the server's
        // FK-validation + audit-event churn stays scoped to real edits.
        const original = isEdit ? (existing?.job ?? null) : null;
        const sendIfChanged = (key: string, next: string | null, prev: string | null) => {
            if (!isEdit) {
                if (next) payload[key] = next;
                return;
            }
            if (prev !== next) payload[key] = next;
        };
        sendIfChanged("customerId", customerId || null, original?.customer_id ?? null);
        sendIfChanged("siteId", siteId || null, original?.site_id ?? null);
        // ─── Scheduling fields, derived from the active strategy ─────
        // Always start by clearing every scheduling field we own. We
        // only re-populate the ones that apply to the chosen strategy
        // — switching from "Fixed appointment" to "Any day" should not
        // leave a stale scheduled_start row in the database.
        payload.schedulingFlexibility = flexibility;
        payload.scheduledStart = null;
        payload.scheduledEnd = null;
        payload.earliestStartAt = null;
        payload.latestFinishAt = null;
        payload.preferredWindow = null;

        if (flexibility === "specific_day") {
            // Soft pin at 08:00 of the chosen day; auto-scheduler treats
            // this as "must land that day, default to morning". Auto-fill
            // end from the job-type duration so dispatch sees a full slot.
            const localStart = `${scheduleDate}T08:00`;
            payload.scheduledStart = new Date(localStart).toISOString();
            const auto = selectedJobType
                ? addMinutesToLocalDatetime(localStart, estimateJobMinutes(selectedJobType))
                : "";
            if (auto) payload.scheduledEnd = new Date(auto).toISOString();
        } else if (flexibility === "specific_window") {
            if (timeOfDayMode === "custom") {
                payload.scheduledStart = new Date(`${scheduleDate}T${customStartTime}`).toISOString();
                payload.scheduledEnd = new Date(`${scheduleDate}T${customEndTime}`).toISOString();
            } else {
                const range = TIME_WINDOW_RANGES[timeOfDayMode];
                payload.earliestStartAt = new Date(`${scheduleDate}T${range.start}`).toISOString();
                payload.latestFinishAt = new Date(`${scheduleDate}T${range.end}`).toISOString();
                payload.preferredWindow = range.preferredWindow;
            }
        } else if (flexibility === "hard_appointment") {
            payload.scheduledStart = new Date(scheduledStart).toISOString();
            payload.scheduledEnd = new Date(scheduledEnd).toISOString();
        }

        // ─── Optional Scheduling Constraints accordion ───────────────
        // The optional `preferredTimeOfDay` chip only applies when the
        // strategy itself doesn't already pin the window
        // (`flexible` or `specific_day`). Otherwise the strategy's own
        // window wins and we keep `preferredWindow` as set above.
        if (preferredTimeOfDay && (flexibility === "flexible" || flexibility === "specific_day")) {
            payload.preferredWindow = preferredTimeOfDay;
        }
        payload.customerNotesScheduling = customerSchedulingNotes.trim() ? customerSchedulingNotes.trim() : null;
        // Empty set or all 7 selected = "any day" → null on the wire.
        const sortedDays = Array.from(allowedWeekdays).sort((a, b) => a - b);
        payload.allowedWeekdays = sortedDays.length === 0 || sortedDays.length === 7 ? null : sortedDays;

        // ─── Compliance deadline ─────────────────────────────────────
        // Only relevant for backflow + fire-safety job types. We still
        // send `null` for non-compliance jobs so a previously-set value
        // is cleared if the job type was reclassified.
        payload.dueBy = isComplianceJobType ? dueBy || null : null;

        // Initial status is derived from whether the job has a fixed
        // start. Specific_window/non-custom and flexible jobs go to
        // `awaiting_scheduling` so the dispatch backlog picks them up.
        if (!isEdit) {
            payload.status = payload.scheduledStart ? "scheduled" : "awaiting_scheduling";
        }

        // Fire the account-number patch in parallel with the job save so
        // the office doesn't wait for two sequential round-trips.
        void patchSiteAccount(accountNumber);
        mutation.mutate(payload);
    };

    const isBackflowTest = selectedJobType?.code === "backflow_test";
    // H6: any backflow job (test, repair, replace) needs a jurisdiction so its
    // result can be filed — not just backflow_test.
    const isBackflowJob = (selectedJobType?.code ?? "").toLowerCase().startsWith("backflow_");
    // Progressive disclosure: the create form opens with just the essentials
    // (Job Type, Customer, Location, then the auto-filled Title). Everything
    // else stays hidden until the core trio is chosen, so the form isn't a
    // wall of fields. Editing an existing job always shows the full form.
    const showAdvanced = isEdit || (!!jobTypeId && !!customerId && !!siteId);
    const platform = siteDetail?.water_provider_submission_platform ?? null;
    const portalUrl = siteDetail?.water_provider_portal_url ?? null;
    // Slice 3: gate the "open portal" deep-link on the server's is_portal (now
    // FK-backed) instead of a hardcoded platform-name set that missed the ~40
    // corrected SCTracking cities (and "Swift Comply", etc.).
    const isPortalPlatform = siteDetail?.water_provider_is_portal === true;
    const isOnline = siteDetail?.water_provider_is_portal === true;

    if (isEdit && loadingJob) return <PageSpinner />;

    return (
        <div className="space-y-6 max-w-2xl mx-auto">
            <div>
                <BackLink
                    fallback="/jobs"
                    fallbackLabel="Back to jobs"
                    className="text-sm text-shell-foreground/80 hover:text-shell-foreground inline-flex items-center gap-1 mb-2"
                    icon={<ArrowLeft className="h-3 w-3" />}
                />
                <h1 className="text-2xl font-black tracking-tight text-shell-foreground">
                    {isEdit ? "Edit Job" : "New Job"}
                </h1>
            </div>

            {sourceEstimate && (
                <Card className="border-green-300 bg-green-50/60 dark:border-green-500/30 dark:bg-green-500/5">
                    <CardContent className="pt-4 pb-4 text-sm space-y-1">
                        <div className="flex items-center gap-2 font-semibold">
                            <Receipt className="h-4 w-4 text-green-700 dark:text-green-400" />
                            Creating from approved estimate
                            {sourceEstimate.display_number ? ` #${sourceEstimate.display_number}` : ""}
                            {sourceEstimate.subtotal != null && (
                                <span className="text-foreground/70 font-normal">
                                    · $
                                    {Number(sourceEstimate.subtotal).toLocaleString(undefined, {
                                        minimumFractionDigits: 2,
                                    })}
                                </span>
                            )}
                        </div>
                        {(sourceEstimate.approved_by_name || sourceEstimate.approved_via_token_at) && (
                            <p className="text-xs text-foreground/80">
                                Approved
                                {sourceEstimate.approved_by_name ? ` by ${sourceEstimate.approved_by_name}` : ""}
                                {sourceEstimate.approved_via_token_at
                                    ? ` on ${new Date(sourceEstimate.approved_via_token_at).toLocaleDateString()}`
                                    : ""}{" "}
                                via the customer email link.
                            </p>
                        )}
                        <p className="text-xs text-foreground/80">
                            Customer, location, title, and job type are prefilled from the estimate — adjust anything
                            before saving. The estimate (and its PDF + approval record) will be linked to the job.{" "}
                            <Link
                                to={`/commercial/${sourceEstimate.id}?type=estimate`}
                                className="text-primary underline"
                            >
                                View estimate
                            </Link>
                        </p>
                    </CardContent>
                </Card>
            )}

            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Job Details</CardTitle>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
                        <div className="space-y-1.5">
                            <label htmlFor="jobType" className="text-sm font-medium">
                                Job Type <span className="text-red-600">*</span>
                            </label>
                            <Select id="jobType" value={jobTypeId} onChange={(e) => setJobTypeId(e.target.value)}>
                                <option value="">Select job type…</option>
                                {jobTypesByTrade.map((group) => (
                                    <optgroup key={group.trade} label={group.trade}>
                                        {group.types.map((jt) => (
                                            <option key={jt.id} value={jt.id}>
                                                {jt.name} · {formatDuration(jt.default_duration_minutes)}
                                            </option>
                                        ))}
                                    </optgroup>
                                ))}
                            </Select>
                            <p className="text-xs text-foreground/80">
                                {selectedJobType
                                    ? `Trade: ${selectedJobType.trade_name} · default time block ${formatDuration(selectedJobType.default_duration_minutes)}.`
                                    : "Job type sets the default scheduled time block. Trade is inferred automatically."}
                            </p>
                        </div>

                        {/* Customer typeahead */}
                        <div className="space-y-1.5">
                            <label className="text-sm font-medium">
                                Customer <span className="text-red-600">*</span>
                            </label>
                            {customerId ? (
                                <div className="flex items-center gap-2 p-2.5 border rounded-lg bg-muted/30">
                                    <Building2 className="h-4 w-4 text-foreground/80" />
                                    <span className="font-medium flex-1">{customerName || customerId}</span>
                                    <button
                                        type="button"
                                        onClick={clearCustomer}
                                        className="p-1 hover:bg-muted rounded"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>
                            ) : (
                                <div className="relative" ref={customerRef}>
                                    <div className="flex gap-2">
                                        <div className="relative flex-1">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/80" />
                                            <Input
                                                placeholder="Search by name, address, or contact…"
                                                value={customerSearch}
                                                onChange={(e) => {
                                                    setCustomerSearch(e.target.value);
                                                    setCustomerDropdownOpen(true);
                                                }}
                                                onFocus={() => setCustomerDropdownOpen(true)}
                                                className="pl-9"
                                            />
                                        </div>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="icon"
                                            onClick={() => setShowNewCustomer(true)}
                                            title="Quick add customer"
                                        >
                                            <Plus className="h-4 w-4" />
                                        </Button>
                                    </div>
                                    {customerDropdownOpen &&
                                        (customerView === "results" || customerView === "recents") &&
                                        customers.length > 0 && (
                                            <div className="absolute z-20 w-full mt-1 bg-card border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                                {customerView === "recents" && (
                                                    <div className="px-3 py-1.5 text-[11px] uppercase tracking-wide text-foreground/80 bg-muted/30 border-b">
                                                        Recent customers
                                                    </div>
                                                )}
                                                {customers.map((c) => (
                                                    <button
                                                        key={c.id}
                                                        type="button"
                                                        className="w-full text-left px-3 py-2.5 hover:bg-muted/50 transition-colors border-b last:border-b-0 text-sm"
                                                        onClick={() => selectCustomer(c)}
                                                    >
                                                        {c.company_name}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    {customerDropdownOpen && customerView === "keep-typing" && (
                                        <div className="absolute z-20 w-full mt-1 bg-card border rounded-lg shadow-lg p-3 text-sm text-foreground/80">
                                            Keep typing — at least 2 letters to search.
                                        </div>
                                    )}
                                    {customerDropdownOpen && customerView === "searching" && (
                                        <div
                                            className="absolute z-20 w-full mt-1 bg-card border rounded-lg shadow-lg p-3 text-sm text-foreground/80"
                                            aria-live="polite"
                                        >
                                            Searching…
                                        </div>
                                    )}
                                    {customerDropdownOpen && customerView === "empty" && (
                                        <div className="absolute z-20 w-full mt-1 bg-card border rounded-lg shadow-lg p-3 text-sm text-foreground/80">
                                            No customers found.{" "}
                                            <button
                                                type="button"
                                                className="text-primary hover:underline"
                                                onClick={() => {
                                                    setShowNewCustomer(true);
                                                    setNewCustomerName(customerSearch);
                                                }}
                                            >
                                                Create new?
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Quick add customer modal */}
                        {showNewCustomer && (
                            <Card className="border-primary/30 bg-primary/5">
                                <CardContent className="pt-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="text-sm font-semibold">Quick Add Customer</span>
                                        <button
                                            type="button"
                                            onClick={() => setShowNewCustomer(false)}
                                            className="p-1 hover:bg-muted rounded"
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>
                                    <div className="flex gap-2">
                                        <Input
                                            placeholder="Company name"
                                            value={newCustomerName}
                                            onChange={(e) => setNewCustomerName(e.target.value)}
                                            className="flex-1"
                                        />
                                        <Button
                                            type="button"
                                            size="sm"
                                            onClick={() => {
                                                if (newCustomerName.trim())
                                                    createCustomerMutation.mutate(newCustomerName.trim());
                                            }}
                                            disabled={!newCustomerName.trim() || createCustomerMutation.isPending}
                                        >
                                            {createCustomerMutation.isPending ? "Creating…" : "Create"}
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        <div className="space-y-1.5">
                            <label className="text-sm font-medium">
                                Site / Location <span className="text-red-600">*</span>
                            </label>
                            {!customerId ? (
                                <div className="p-2.5 border rounded-lg bg-muted/30 text-sm text-foreground/70">
                                    Choose a customer first
                                </div>
                            ) : siteId ? (
                                <div className="flex items-center gap-2 p-2.5 border rounded-lg bg-muted/30">
                                    <MapPin className="h-4 w-4 text-foreground/80" />
                                    <span className="font-medium flex-1">{selectedSiteLabel}</span>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSiteId("");
                                            setSiteSearch("");
                                            setSiteDropdownOpen(true);
                                        }}
                                        className="p-1 hover:bg-muted rounded"
                                        title="Change location"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>
                            ) : (
                                <div className="relative" ref={siteRef}>
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/80" />
                                        <Input
                                            placeholder="Search locations by name, address, city, or account #…"
                                            value={siteSearch}
                                            onChange={(e) => {
                                                setSiteSearch(e.target.value);
                                                setSiteDropdownOpen(true);
                                            }}
                                            onFocus={() => setSiteDropdownOpen(true)}
                                            className="pl-9"
                                        />
                                    </div>
                                    {siteDropdownOpen && sites.length > 0 && (
                                        <div className="absolute z-20 w-full mt-1 bg-card border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                            {sites.map((s: SiteOption) => (
                                                <button
                                                    key={s.id}
                                                    type="button"
                                                    className="w-full text-left px-3 py-2.5 hover:bg-muted/50 transition-colors border-b last:border-b-0 text-sm"
                                                    onClick={() => {
                                                        setSiteId(s.id);
                                                        setSiteDropdownOpen(false);
                                                        setSiteSearch("");
                                                    }}
                                                >
                                                    <span className="font-medium">{s.name}</span>
                                                    {(s.address_line || s.city) && (
                                                        <span className="block text-xs text-foreground/70">
                                                            {[s.address_line, s.city].filter(Boolean).join(", ")}
                                                        </span>
                                                    )}
                                                </button>
                                            ))}
                                            {(sitesData?.total ?? 0) > sites.length && (
                                                <div className="px-3 py-2 text-[11px] text-foreground/60 bg-muted/30 border-t">
                                                    Showing {sites.length} of {sitesData?.total}. Keep typing to narrow.
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {siteDropdownOpen && debouncedSiteSearch && sites.length === 0 && (
                                        <div className="absolute z-20 w-full mt-1 bg-card border rounded-lg shadow-lg p-3 text-sm text-foreground/80">
                                            No locations match “{debouncedSiteSearch}”.
                                        </div>
                                    )}
                                    {!debouncedSiteSearch && (sitesData?.total ?? 0) === 0 && (
                                        <div className="mt-1.5 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-700/60 dark:bg-amber-950/30 p-2.5 text-xs text-amber-900 dark:text-amber-200">
                                            This customer has no locations yet. A job needs a service address.
                                            {/* G5: same-tab; cmd/ctrl+click for a new tab. */}
                                            <Link
                                                to={`/customers/${customerId}`}
                                                className="ml-1 font-medium underline hover:no-underline"
                                            >
                                                Add a location →
                                            </Link>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="space-y-1.5">
                            <label htmlFor="title" className="text-sm font-medium">
                                Title <span className="text-destructive">*</span>
                            </label>
                            <Input
                                id="title"
                                required
                                placeholder="e.g. Annual Backflow Test — 123 Main St"
                                value={title}
                                onChange={(e) => {
                                    setTitleManuallyEdited(true);
                                    setTitle(e.target.value);
                                }}
                            />
                            <p className="text-xs text-foreground/80">
                                Auto-filled from the job type and location. Edit if you want a custom title. This is the
                                tech&rsquo;s headline — the first and biggest thing they read on their phone.
                            </p>
                            {/* #823 — hand-typed "(N)" counts in titles are how 4 of
                                ~30 board jobs came to contradict their real device
                                lists. The system counts devices itself; say so here,
                                where the habit starts. */}
                            {isComplianceJobType &&
                                (() => {
                                    const n = (siteAssetsData?.assets ?? []).filter((a) => !a.parent_asset_id).length;
                                    return n > 0 ? (
                                        <p className="text-xs text-primary">
                                            {n} device{n === 1 ? "" : "s"} at this site will ride along automatically —
                                            no need to type a count into the title.
                                        </p>
                                    ) : null;
                                })()}
                        </div>

                        {!showAdvanced && (
                            <p className="text-xs text-foreground/70">
                                Pick a job type, customer, and location to fill in scheduling, contact, and the rest.
                            </p>
                        )}

                        {showAdvanced && (
                            <>
                                {/* ─── Summary — the tech's "what you're doing here" (#813) ── */}
                                {/* Create-only: after the job exists this is edited on the
                            job page's Summary card ("SUMMARY — THE ASK"). The tech
                            field app reads it FIRST (reference column top), so the
                            person creating the job writes it while the details are
                            in front of them instead of it costing a phone call. */}
                                {!isEdit && (
                                    <div className="space-y-1.5">
                                        <label htmlFor="summary" className="text-sm font-medium">
                                            Summary — what to do on site
                                        </label>
                                        <Textarea
                                            id="summary"
                                            rows={3}
                                            placeholder="e.g. Test both RPZs — one at the meter, one behind the building. Ask for Maria at the front desk."
                                            value={summary}
                                            onChange={(e) => setSummary(e.target.value)}
                                        />
                                        <p className="text-xs text-foreground/80">
                                            The tech sees this first — one or two sentences saying exactly what needs to
                                            happen on site. (Gate codes and hazards go in Job Notes below; this is the
                                            ask itself. On the job page this field is called &ldquo;Summary — the
                                            ask.&rdquo;)
                                        </p>
                                        {/* The mirror principle: show the setter the tech's view. */}
                                        {(title.trim() || summary.trim()) && (
                                            <div className="rounded-lg border border-border bg-muted/30 p-3">
                                                <p className="text-[11px] uppercase tracking-wide text-foreground/60 mb-1.5">
                                                    What your tech will see
                                                </p>
                                                <p className="text-base font-semibold leading-snug">
                                                    {title.trim() || "(no title yet)"}
                                                </p>
                                                {summary.trim() ? (
                                                    <p className="mt-1 text-sm whitespace-pre-wrap">{summary.trim()}</p>
                                                ) : (
                                                    <p className="mt-1 text-sm italic text-foreground/60">
                                                        No summary — the tech&rsquo;s &ldquo;what you&rsquo;re doing
                                                        here&rdquo; card will fall back to auto-generated text or sit
                                                        empty.
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {/* ─── Customer PO / WO + statement description (#887) ── */}
                                {/* Create-only: maintenance accounts (Cintas etc.) pay
                            against their own WO number, so the job is born with it
                            instead of someone remembering the Details-card edit
                            later. After create these live on the Details card. */}
                                {!isEdit && (
                                    <div className="space-y-1.5">
                                        <label className="text-sm font-medium">
                                            Customer&rsquo;s reference numbers (optional)
                                        </label>
                                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                            <input
                                                type="text"
                                                maxLength={120}
                                                className="rounded-md border bg-background px-3 py-2 text-sm"
                                                placeholder="Customer PO #"
                                                value={customerPo}
                                                onChange={(e) => setCustomerPo(e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                maxLength={120}
                                                className="rounded-md border bg-background px-3 py-2 text-sm"
                                                placeholder="Work order # (their WO)"
                                                value={externalRef}
                                                onChange={(e) => setExternalRef(e.target.value)}
                                            />
                                        </div>
                                        <input
                                            type="text"
                                            maxLength={200}
                                            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                                            placeholder="Customer description — one line for statements/invoices (optional)"
                                            value={customerFacingDescription}
                                            onChange={(e) => setCustomerFacingDescription(e.target.value)}
                                        />
                                        <p className="text-xs text-foreground/80">
                                            These print on the invoice so the customer&rsquo;s office can match it to
                                            their records. The description is the one line they&rsquo;ll read — door
                                            codes and urgency belong in the notes, not here.
                                        </p>
                                    </div>
                                )}
                                {/* Location contact — read-only. The contact follows the
                            location (sites.default_contact_id) and is managed on
                            the location page, not per-job, so every job at the
                            site always shows the current on-site contact. */}
                                {customerId && siteId && (
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Location contact</label>
                                        <div className="rounded-md border border-border p-3 text-sm">
                                            {siteDetail?.default_contact_id ? (
                                                <div className="space-y-0.5">
                                                    <div className="font-medium">
                                                        {[
                                                            siteDetail.site_contact_first_name,
                                                            siteDetail.site_contact_last_name,
                                                        ]
                                                            .filter(Boolean)
                                                            .join(" ") || "Contact"}
                                                        {siteDetail.site_contact_role ? (
                                                            <span className="text-muted-foreground">
                                                                {" "}
                                                                ({siteDetail.site_contact_role})
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                    {siteDetail.site_contact_phone && (
                                                        <div className="text-xs text-muted-foreground">
                                                            {siteDetail.site_contact_phone}
                                                        </div>
                                                    )}
                                                    {siteDetail.site_contact_email && (
                                                        <div className="text-xs text-muted-foreground break-all">
                                                            {siteDetail.site_contact_email}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-muted-foreground">
                                                    No on-site contact set for this location.
                                                </span>
                                            )}
                                            <Link
                                                to={`/sites/${siteId}`}
                                                className="mt-2 inline-block text-xs text-primary underline"
                                            >
                                                Manage on location
                                            </Link>
                                        </div>
                                    </div>
                                )}

                                {/* Reporting Setup — only shown for Backflow Test. During intake the
                            office needs to know which agency receives the test, whether the
                            jurisdiction uses a portal, and the account number so the tech
                            doesn't have to call the office mid-test. */}
                                {isBackflowJob && siteId && siteDetail && (
                                    <Card className="border-sky-200 bg-sky-50/40 dark:border-sky-500/20 dark:bg-sky-500/5">
                                        <CardHeader className="pb-3">
                                            <CardTitle className="text-sm flex items-center gap-2">
                                                <Info className="h-4 w-4 text-sky-600" />
                                                Reporting setup
                                                {platform && (
                                                    <span className="ml-1 rounded-full bg-sky-600/10 px-2 py-0.5 text-xs font-medium text-sky-700 dark:text-sky-300">
                                                        {platform}
                                                    </span>
                                                )}
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent className="space-y-3 text-sm">
                                            {!siteDetail.water_provider_id ? (
                                                <div className="flex items-start gap-2 text-amber-700 dark:text-amber-400">
                                                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                                                    <div>
                                                        <p className="font-medium">
                                                            No jurisdiction set for this site.
                                                        </p>
                                                        <p className="text-xs text-foreground/80">
                                                            Open the site record and pick a water provider so the tech
                                                            knows where this test report gets submitted.
                                                        </p>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div>
                                                    <p>
                                                        <span className="text-foreground/80">Reports to:</span>{" "}
                                                        <span className="font-medium">
                                                            {siteDetail.water_provider_name ?? "—"}
                                                        </span>
                                                        {platform && (
                                                            <span className="text-foreground/80">
                                                                {" "}
                                                                via{" "}
                                                                <span className="font-medium text-foreground">
                                                                    {platform}
                                                                </span>
                                                            </span>
                                                        )}
                                                    </p>
                                                    {/* #825 M11: say what the platform IS — "via Vepo"
                                                assumed a day-one hire knows the vendor name. */}
                                                    {platform && isPortalPlatform && (
                                                        <p className="text-xs text-foreground/60">
                                                            {platform} is the online reporting portal this city&apos;s
                                                            test reports are filed through.
                                                        </p>
                                                    )}
                                                    {isPortalPlatform && (
                                                        <div className="mt-2">
                                                            {portalUrl ? (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => openExternalLink(portalUrl)}
                                                                    className="inline-flex items-center gap-1.5 text-sm font-medium text-sky-700 hover:underline dark:text-sky-300"
                                                                >
                                                                    <ExternalLink className="h-3.5 w-3.5" />
                                                                    Open {platform} portal
                                                                </button>
                                                            ) : (
                                                                <span className="text-xs text-foreground/80">
                                                                    No portal URL saved for {platform}. Add it in Admin
                                                                    → Water providers.
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {siteDetail.water_provider_id && !isOnline && (
                                                <p className="text-xs text-foreground/60">
                                                    This jurisdiction reports offline — no portal account number needed.
                                                </p>
                                            )}
                                            {isOnline && (
                                                <div className="space-y-1.5">
                                                    <label htmlFor="accountNumber" className="text-sm font-medium">
                                                        {platform ? `${platform} account number` : "Account number"}
                                                    </label>
                                                    {/* #825 M11: a stored account is read-only until Edit —
                                                it's a live compliance identifier, not a form field. */}
                                                    {siteDetail.water_account_number && !editingAccount ? (
                                                        <div className="flex items-center gap-2">
                                                            <span
                                                                className="rounded-md border bg-muted/50 px-3 py-1.5 font-mono text-sm tabular-nums"
                                                                data-testid="account-number-locked"
                                                            >
                                                                {accountNumber || siteDetail.water_account_number}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                className="text-xs font-medium text-primary hover:underline"
                                                                onClick={() => setEditingAccount(true)}
                                                            >
                                                                Edit
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <Input
                                                            id="accountNumber"
                                                            placeholder={
                                                                siteDetail.water_account_number
                                                                    ? "On file from this site"
                                                                    : "Ask the customer, or look it up in the portal"
                                                            }
                                                            value={accountNumber}
                                                            onChange={(e) => setAccountNumber(e.target.value)}
                                                        />
                                                    )}
                                                    <p className="text-xs text-foreground/80">
                                                        {siteDetail.water_account_number
                                                            ? "Saved on this address. Edit only if the customer corrects it — filings use this number."
                                                            : "Captured here will save on the address so every future job reuses it. (Best captured at intake when the customer is on the call.)"}
                                                    </p>
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>
                                )}

                                {/* ─── Scheduling ──────────────────────────────────────────
                            Decision-tree layout: dispatcher first answers
                            "How flexible is this job?" via the strategy
                            cards, then only the fields relevant to that
                            strategy appear. Compliance Scheduling sits
                            above the strategy cards because the
                            regulator's clock is the primary constraint
                            for backflow + fire-safety jobs. */}
                                <Card>
                                    <CardHeader className="pb-3">
                                        <CardTitle className="text-sm">Scheduling Strategy</CardTitle>
                                        <p className="text-xs text-foreground/80">How flexible is this job?</p>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        {/* Compliance Scheduling — only for backflow + fire-safety job types */}
                                        {isComplianceJobType && (
                                            <div className="rounded-lg border border-amber-200/70 bg-amber-50/70 p-3 dark:border-amber-500/30 dark:bg-amber-500/10 space-y-2">
                                                <div className="flex items-center gap-2 text-sm font-semibold">
                                                    <ShieldCheck className="h-4 w-4 text-amber-700 dark:text-amber-400" />
                                                    Compliance Scheduling
                                                </div>
                                                <div className="space-y-1.5">
                                                    <label htmlFor="dueBy" className="text-sm font-medium">
                                                        Due By Date <span className="text-destructive">*</span>
                                                    </label>
                                                    <Input
                                                        id="dueBy"
                                                        type="date"
                                                        value={dueBy}
                                                        onChange={(e) => {
                                                            dueByTouchedRef.current = true;
                                                            setDueBy(e.target.value);
                                                        }}
                                                        required
                                                    />
                                                    <p className="text-xs text-foreground/80">
                                                        Required for compliance scheduling and overdue tracking.
                                                    </p>
                                                    {autoDueByHint && !dueByTouchedRef.current && (
                                                        <p className="text-xs text-primary">
                                                            Auto-filled from device next-due ({autoDueByHint}) — edit to
                                                            override.
                                                        </p>
                                                    )}
                                                    {isReplacementJobType && (
                                                        <p className="text-xs text-foreground/80">
                                                            Replacement — pick the date the swap must be done by. (Not
                                                            auto-filled: the old device&rsquo;s test due date
                                                            isn&rsquo;t the swap deadline.)
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Strategy cards */}
                                        <div className="grid gap-2 sm:grid-cols-2">
                                            {FLEX_OPTIONS.map((opt) => {
                                                const selected = flexibility === opt.value;
                                                return (
                                                    <button
                                                        key={opt.value}
                                                        type="button"
                                                        onClick={() => setFlexibility(opt.value)}
                                                        aria-pressed={selected}
                                                        className={`rounded-lg border p-3 text-left transition ${
                                                            selected
                                                                ? "border-primary bg-primary/5 ring-1 ring-primary/30"
                                                                : "border-border hover:border-primary/40 hover:bg-muted/40"
                                                        }`}
                                                    >
                                                        <div className="text-sm font-semibold">{opt.label}</div>
                                                        <p className="mt-1 text-xs text-foreground/80">{opt.helper}</p>
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        {/* Mode-specific fields — progressive disclosure */}
                                        {flexibility === "specific_day" && (
                                            <div className="space-y-1.5">
                                                <label htmlFor="specificDate" className="text-sm font-medium">
                                                    Date <span className="text-destructive">*</span>
                                                </label>
                                                <Input
                                                    id="specificDate"
                                                    type="date"
                                                    value={scheduleDate}
                                                    onChange={(e) => setScheduleDate(e.target.value)}
                                                    required
                                                />
                                            </div>
                                        )}

                                        {flexibility === "specific_window" && (
                                            <div className="space-y-3">
                                                <div className="space-y-1.5">
                                                    <label htmlFor="windowDate" className="text-sm font-medium">
                                                        Date <span className="text-destructive">*</span>
                                                    </label>
                                                    <Input
                                                        id="windowDate"
                                                        type="date"
                                                        value={scheduleDate}
                                                        onChange={(e) => setScheduleDate(e.target.value)}
                                                        required
                                                    />
                                                </div>
                                                <div className="space-y-1.5">
                                                    <label className="text-sm font-medium">Time of day</label>
                                                    <div className="flex flex-wrap gap-1.5">
                                                        {TIME_OF_DAY_OPTIONS.map((opt) => {
                                                            const on = timeOfDayMode === opt.value;
                                                            return (
                                                                <button
                                                                    key={opt.value}
                                                                    type="button"
                                                                    onClick={() => setTimeOfDayMode(opt.value)}
                                                                    aria-pressed={on}
                                                                    className={`rounded-md border px-3 py-1.5 text-xs font-medium transition ${
                                                                        on
                                                                            ? "border-primary bg-primary/10 text-primary"
                                                                            : "border-border bg-background text-foreground/70 hover:border-primary/40 hover:text-foreground"
                                                                    }`}
                                                                >
                                                                    {opt.label}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                                {timeOfDayMode === "custom" && (
                                                    <div className="grid gap-3 sm:grid-cols-2">
                                                        <div className="space-y-1.5">
                                                            <label
                                                                htmlFor="customStart"
                                                                className="text-sm font-medium"
                                                            >
                                                                Start Time <span className="text-destructive">*</span>
                                                            </label>
                                                            <Input
                                                                id="customStart"
                                                                type="time"
                                                                value={customStartTime}
                                                                onChange={(e) => setCustomStartTime(e.target.value)}
                                                                required
                                                            />
                                                        </div>
                                                        <div className="space-y-1.5">
                                                            <label htmlFor="customEnd" className="text-sm font-medium">
                                                                End Time <span className="text-destructive">*</span>
                                                            </label>
                                                            <Input
                                                                id="customEnd"
                                                                type="time"
                                                                value={customEndTime}
                                                                onChange={(e) => setCustomEndTime(e.target.value)}
                                                                required
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {flexibility === "hard_appointment" && (
                                            <div className="grid gap-3 sm:grid-cols-2">
                                                <div className="space-y-1.5">
                                                    <label htmlFor="hardStart" className="text-sm font-medium">
                                                        Start Date/Time <span className="text-destructive">*</span>
                                                    </label>
                                                    <Input
                                                        id="hardStart"
                                                        type="datetime-local"
                                                        value={scheduledStart}
                                                        onChange={(e) => setScheduledStart(e.target.value)}
                                                        required
                                                    />
                                                </div>
                                                <div className="space-y-1.5">
                                                    <label htmlFor="hardEnd" className="text-sm font-medium">
                                                        End Date/Time <span className="text-destructive">*</span>
                                                    </label>
                                                    <Input
                                                        id="hardEnd"
                                                        type="datetime-local"
                                                        value={scheduledEnd}
                                                        onChange={(e) => {
                                                            scheduledEndTouchedRef.current = true;
                                                            setScheduledEnd(e.target.value);
                                                        }}
                                                        required
                                                    />
                                                    {selectedJobType &&
                                                        !scheduledEndTouchedRef.current &&
                                                        scheduledStart && (
                                                            <p className="text-xs text-foreground/80">
                                                                Auto-set to{" "}
                                                                {formatDuration(estimateJobMinutes(selectedJobType))} (
                                                                {selectedJobType.name} estimate).
                                                            </p>
                                                        )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Optional Scheduling Constraints accordion.
                                    Collapsed by default unless we already have
                                    saved values for any of these fields, so the
                                    dispatcher only sees them when they matter. */}
                                        <div className="rounded-md border border-border/70 bg-muted/30">
                                            <button
                                                type="button"
                                                onClick={() => setConstraintsOpen((v) => !v)}
                                                aria-expanded={constraintsOpen}
                                                className="flex w-full items-center gap-2 px-3 py-2.5 text-left"
                                            >
                                                <ChevronDown
                                                    className={`h-4 w-4 transition-transform ${constraintsOpen ? "" : "-rotate-90"}`}
                                                />
                                                <div className="flex-1">
                                                    <div className="text-sm font-semibold">
                                                        Optional Scheduling Constraints
                                                    </div>
                                                    <p className="text-xs text-foreground/80">
                                                        Add limits only if the customer or compliance rules require them
                                                        — e.g. &ldquo;not before 9 AM — school zone&rdquo;,
                                                        &ldquo;Tuesdays only&rdquo;, or an earliest/latest date.
                                                    </p>
                                                </div>
                                            </button>
                                            {constraintsOpen && (
                                                <div className="space-y-3 px-3 pb-3">
                                                    <div className="space-y-1.5">
                                                        <label className="text-sm font-medium">
                                                            Preferred weekdays
                                                        </label>
                                                        <div className="flex flex-wrap gap-1.5">
                                                            {WEEKDAY_CHIPS.map((d) => {
                                                                const on = allowedWeekdays.has(d.value);
                                                                return (
                                                                    <button
                                                                        key={d.value}
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setAllowedWeekdays((prev) => {
                                                                                const next = new Set(prev);
                                                                                if (next.has(d.value))
                                                                                    next.delete(d.value);
                                                                                else next.add(d.value);
                                                                                return next;
                                                                            });
                                                                        }}
                                                                        className={`rounded-md border px-2.5 py-1 text-xs font-medium transition ${
                                                                            on
                                                                                ? "border-primary bg-primary/10 text-primary"
                                                                                : "border-border bg-background text-foreground/70 hover:border-primary/40 hover:text-foreground"
                                                                        }`}
                                                                        aria-pressed={on}
                                                                        aria-label={d.long}
                                                                    >
                                                                        {d.short}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                        <p className="text-xs text-foreground/80">
                                                            {allowedWeekdays.size === 0 || allowedWeekdays.size === 7
                                                                ? "Any day works."
                                                                : `Customer is available ${allowedWeekdays.size === 1 ? "only " : ""}on ${WEEKDAY_CHIPS.filter(
                                                                      (d) => allowedWeekdays.has(d.value),
                                                                  )
                                                                      .map((d) => d.short)
                                                                      .join(", ")}.`}
                                                        </p>
                                                    </div>

                                                    {(flexibility === "flexible" || flexibility === "specific_day") && (
                                                        <div className="space-y-1.5">
                                                            <label className="text-sm font-medium">
                                                                Preferred time of day
                                                            </label>
                                                            <div className="flex flex-wrap gap-1.5">
                                                                {(
                                                                    [
                                                                        { value: "AM", label: "Morning" },
                                                                        { value: "PM", label: "Afternoon" },
                                                                        { value: "ANYTIME", label: "All day" },
                                                                    ] as const
                                                                ).map((opt) => {
                                                                    const on = preferredTimeOfDay === opt.value;
                                                                    return (
                                                                        <button
                                                                            key={opt.value}
                                                                            type="button"
                                                                            onClick={() =>
                                                                                setPreferredTimeOfDay(
                                                                                    on ? "" : opt.value,
                                                                                )
                                                                            }
                                                                            className={`rounded-md border px-3 py-1 text-xs font-medium transition ${
                                                                                on
                                                                                    ? "border-primary bg-primary/10 text-primary"
                                                                                    : "border-border bg-background text-foreground/70 hover:border-primary/40 hover:text-foreground"
                                                                            }`}
                                                                            aria-pressed={on}
                                                                        >
                                                                            {opt.label}
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>
                                                            <p className="text-xs text-foreground/80">
                                                                Soft hint to the auto-scheduler when the day is
                                                                flexible.
                                                            </p>
                                                        </div>
                                                    )}

                                                    <div className="space-y-1.5">
                                                        <label
                                                            htmlFor="customerSchedNotes"
                                                            className="text-sm font-medium"
                                                        >
                                                            Customer scheduling notes
                                                        </label>
                                                        <Textarea
                                                            id="customerSchedNotes"
                                                            rows={2}
                                                            placeholder="e.g. 'Call 30 min before'  ·  'Back gate code 1234, locked after 5pm'  ·  'Bar opens at 4pm — be done before then'"
                                                            value={customerSchedulingNotes}
                                                            onChange={(e) => setCustomerSchedulingNotes(e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* ─── Job Notes ───────────────────────────────────────── */}
                                {/* Notes are an append-only thread on the job page now.
                            The form only seeds the FIRST note at create time —
                            editing here would overwrite thread history, so on
                            edit we point at the thread instead. */}
                                {isEdit ? (
                                    <div className="space-y-1.5">
                                        <p className="text-sm font-medium">Job Notes</p>
                                        <p className="text-xs text-foreground/80">
                                            Notes live on the job page now — an append-only thread where every entry
                                            keeps its author and timestamp. Add notes from the job page&rsquo;s Activity
                                            card.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="space-y-1.5">
                                        <label htmlFor="notes" className="text-sm font-medium">
                                            Job Notes
                                        </label>
                                        <Textarea
                                            id="notes"
                                            rows={4}
                                            placeholder={
                                                isBackflowTest
                                                    ? "Gate codes, hazards, contact preferences, which assembly to test, anything the tech should know…"
                                                    : "Instructions for the tech, special conditions, anything relevant to this job…"
                                            }
                                            value={notes}
                                            onChange={(e) => setNotes(e.target.value)}
                                        />
                                        <p className="text-xs text-foreground/80">
                                            Becomes the first entry of the job&rsquo;s notes thread — visible to the
                                            tech on arrival, in the workflow, and on the job detail page.
                                        </p>
                                    </div>
                                )}
                            </>
                        )}

                        {mutation.isError && !holdConflict && (
                            <p className="text-sm text-destructive">Failed to save job. Please try again.</p>
                        )}

                        <div className="flex items-center gap-3 pt-2">
                            <Button type="submit" disabled={mutation.isPending}>
                                <Save className="h-4 w-4" />
                                {mutation.isPending ? "Saving…" : isEdit ? "Update Job" : "Create Job"}
                            </Button>
                            <Button type="button" variant="outline" onClick={() => navigate("/jobs")}>
                                Cancel
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>

            {/* #1030 — "Do Not Service" warning. A manager may work the job
                anyway by saying why (recorded against the account); everyone
                else is stopped here and told who to ask. */}
            <Dialog
                open={holdConflict !== null}
                onClose={() => setHoldConflict(null)}
                title="This account is on service hold"
            >
                <div className="space-y-3">
                    <div className="flex items-start gap-2">
                        <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                        <div className="text-sm">
                            <p className="font-medium break-words">
                                {holdConflict?.reason?.trim() || "No reason was recorded when this hold was placed."}
                            </p>
                            {(holdConflict?.setByName || holdConflict?.setAt) && (
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    {holdConflict?.setByName ? `Held by ${holdConflict.setByName}` : "Held"}
                                    {holdConflict?.setAt
                                        ? ` on ${new Date(holdConflict.setAt).toLocaleDateString()}`
                                        : ""}
                                </p>
                            )}
                        </div>
                    </div>

                    {holdConflict?.canOverride ? (
                        <>
                            <div>
                                <label className="text-sm font-medium" htmlFor="job-hold-override">
                                    Create it anyway? Say why — it's recorded against the account.
                                </label>
                                <Textarea
                                    id="job-hold-override"
                                    rows={3}
                                    value={holdOverrideReason}
                                    onChange={(e) => setHoldOverrideReason(e.target.value)}
                                    placeholder="Prepaid in cash before the visit."
                                />
                            </div>
                            <div className="flex justify-end gap-2">
                                <Button type="button" variant="outline" onClick={() => setHoldConflict(null)}>
                                    Don't create it
                                </Button>
                                <Button
                                    type="button"
                                    disabled={!holdOverrideReason.trim() || mutation.isPending}
                                    onClick={() => {
                                        const conflict = holdConflict;
                                        if (!conflict) return;
                                        setHoldConflict(null);
                                        mutation.mutate({
                                            ...conflict.payload,
                                            serviceHoldOverrideReason: holdOverrideReason.trim(),
                                        });
                                    }}
                                >
                                    Create the job anyway
                                </Button>
                            </div>
                        </>
                    ) : (
                        <>
                            <p className="text-sm text-muted-foreground">
                                Only a manager or admin can work a held account. Ask one to release the hold, or to
                                create this job for you.
                            </p>
                            <div className="flex justify-end">
                                <Button type="button" variant="outline" onClick={() => setHoldConflict(null)}>
                                    Close
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </Dialog>
        </div>
    );
}
